import { useSongs } from "@/services/reporting";
import { fetchSong, updateContent } from "@/services/api";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Stack, Typography, IconButton, CardMedia } from "@mui/material";
import { Edit } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { formatSeconds } from "@/utils";
import { useNavigate } from "react-router-dom";
import NotebookIcon from "@/fragments/NotebookIcon";

const SHARE_BASE = "https://storage.googleapis.com/notebook-fm-cdn-prod/";

export default function Songs() {
  const { data, isLoading, error } = useSongs();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const processRowUpdate = async (newRow, oldRow) => {
    if (
      newRow.writers === oldRow.writers &&
      newRow.producers === oldRow.producers &&
      newRow.usage_id === oldRow.usage_id &&
      newRow.label === oldRow.label
    ) {
      return oldRow;
    }
    const content = await fetchSong(newRow.content_key);
    content.meta.writers = newRow.writers;
    content.meta.producers = newRow.producers;
    content.meta.usage_id = newRow.usage_id;
    content.meta.label = newRow.label;
    await updateContent(content);
    queryClient.invalidateQueries({ queryKey: ["songs"] });
    return newRow;
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading data</div>;

  const columns = [
    {
      field: "title",
      headerName: "Title",
      width: 250,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
          <CardMedia
            component="img"
            src={row.cover || row.artist_artwork || row.artist_profile}
            sx={{ width: 40, height: 40, borderRadius: 1 }}
          />
          <Typography variant="body2" sx={{ ml: 2, fontWeight: 600 }}>
            {row.title}
          </Typography>
        </Box>
      ),
    },
    { field: "artist_name", headerName: "Artist", width: 150 },
    // { field: "artist_key", width: 150 },
    { field: "writers", headerName: "Written by", width: 200, editable: true },
    {
      field: "producers",
      headerName: "Produced by",
      width: 150,
      editable: true,
    },
    {
      field: "duration",
      headerName: "Duration",
      width: 100,
      type: "number",
      valueFormatter: (value) => formatSeconds(value),
    },
    {
      field: "created",
      width: 100,
      valueFormatter: (value) =>
        new Date(value * 1000).toISOString().split("T")[0],
    },
    {
      field: "usage_id",
      headerName: "Usage Identifier",
      valueGetter: (value, row) => row.usage_id || row.content_id,
      width: 100,
      editable: true,
    },
    {
      field: "label",
      headerName: "Label Name",
      width: 150,
      editable: true,
    },
    {
      field: "content_key",
      headerName: "DSP Recording Identifier",
      width: 100,
      valueFormatter: (value) => value?.replaceAll("-", ""),
    },
    {
      field: "share_url",
      headerName: "Share URL",
      width: 100,
      valueGetter: (value, row) => `${SHARE_BASE}${row.content_key}`,
    },
  ];

  return (
    <Box>
      <Stack direction={"row"} spacing={2} alignItems={"center"} mb={2}>
        <NotebookIcon size={24} />
        <Typography
          style={{
            fontFamily: "'Montserrat Alternates', sans-serif",
            fontWeight: 700,
            fontSize: "1.8rem",
            marginBottom: "2px",
            marginLeft: "20px",
          }}
          gutterBottom
        >
          songs
        </Typography>
      </Stack>
      <Box sx={{ height: "calc(100vh - 200px)" }}>
        <DataGrid
          rows={data ?? []}
          columns={columns}
          getRowId={(row) => row.content_key}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(err) => alert(err.message)}
          showToolbar
          initialState={{
            sorting: {
              sortModel: [{ field: "created", sort: "desc" }],
            },
          }}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: `${Math.floor(Date.now() / 1000)}-notebook-songs-report`,
                delimiter: ",",
                utf8WithBom: true,
              },
            },
          }}
        />
      </Box>
    </Box>
  );
}
